export const OTA_BUSTER_ENTRY = {
  title: 'Guests booked via OTA',
  description:
    'guests booked through an OTA and are ready to start their guest journey.'
}

export const OTA_BUSTER_PERIOD_OPTIONS = [
  { id: '15', label: 'Last 15 days', days: 15 },
  { id: '30', label: 'Last 30 days', days: 30 },
  { id: '90', label: 'Last 90 days', days: 90 }
]

export const OTA_BUSTER_CAMPAIGN_STRATEGY = {
  title: 'Campaign strategy',
  subtitle: 'How guests are messaged',
  channelLabel: 'Email Only - Across Staged'
}

export const EMPTY_STEP_METRICS = {
  guests: 0,
  emailsSent: 0,
  clickThroughRate: 0,
  identityCaptures: 0,
  engagementRate: 0
}

/**
 * Step definitions for the Campaign Flow UI.
 * Timing for pre-check-in steps is editable; event-based steps stay fixed labels.
 * Each step is independently enableable (unlike Site Abandonment chain rules).
 * Previews here are only the fallback when the hotel has no saved message template yet.
 */
export const OTA_BUSTER_CAMPAIGN_STEPS = [
  {
    id: 'just-booked',
    contentCategory: 'OtaBusterJustBooked',
    timingKind: 'event',
    timingLabel: 'Immediately after booking',
    title: 'Just Booked',
    stepNumber: 1,
    offerLabel: 'No offer attached',
    iconColor: '#7c5cbf',
    preview:
      "Hi {first}, thanks for booking {hotel}! Here's your confirmation and a few quick details.",
    defaultDaysBeforeCheckIn: null
  },
  {
    id: 'pre-check-in',
    contentCategory: 'OtaBusterPreCheckIn',
    timingKind: 'daysBeforeCheckIn',
    timingLabel: 'Wait 3 days before arrival',
    title: 'Pre-Check-in',
    stepNumber: 2,
    offerLabel: 'Room upgrade',
    hasRoomUpgradeOffer: true,
    iconColor: '#2f9e74',
    preview:
      'Hi {first}, your stay at {hotel} is just 3 days away. Confirm a few details before you arrive.',
    defaultDaysBeforeCheckIn: 3
  },
  {
    id: 'pre-check-in-reminder',
    contentCategory: 'OtaBusterPreCheckInReminder',
    timingKind: 'daysBeforeCheckIn',
    timingLabel: '1 day before check-in',
    title: 'Pre-Check-in Reminder',
    stepNumber: 3,
    offerLabel: 'No offer attached',
    iconColor: '#d4a017',
    preview:
      "Hi {first}, we can't wait to welcome you tomorrow. Here's what to expect at arrival.",
    defaultDaysBeforeCheckIn: 1
  },
  {
    id: 'post-check-in',
    contentCategory: 'OtaBusterPostCheckIn',
    timingKind: 'event',
    timingLabel: 'Just after check-in',
    title: 'Post-Check-in',
    stepNumber: 4,
    offerLabel: 'No offer attached',
    iconColor: '#2f6fed',
    preview:
      "Hi {first}, welcome to {hotel}! Here's everything you need for a great stay.",
    defaultDaysBeforeCheckIn: null
  },
  {
    id: 'post-checkout',
    contentCategory: 'OtaBusterPostCheckout',
    timingKind: 'event',
    timingLabel: 'Just after checkout',
    title: 'Post-Checkout',
    stepNumber: 5,
    offerLabel: 'No offer attached',
    iconColor: '#d4537e',
    preview:
      "Hi {first}, thank you for staying with us. We'd love to hear about your experience.",
    defaultDaysBeforeCheckIn: null
  }
]

export const OTA_BUSTER_CONTENT_CATEGORIES = OTA_BUSTER_CAMPAIGN_STEPS.map(
  step => step.contentCategory
)

export function getStepById(stepId) {
  return OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.id === stepId) || null
}

export function getStepByCategory(category) {
  return (
    OTA_BUSTER_CAMPAIGN_STEPS.find(step => step.contentCategory === category) || null
  )
}

export function formatTimingLabel(step, daysBeforeCheckIn) {
  if (!step) return ''
  if (step.timingKind !== 'daysBeforeCheckIn') {
    return step.timingLabel
  }

  const days = Number(daysBeforeCheckIn) || step.defaultDaysBeforeCheckIn || 1
  if (days === 1) {
    return 'Wait 1 day before arrival'
  }
  return `Wait ${days} days before arrival`
}

export function getAnalyticsDateRange(periodDays) {
  const days = Number(periodDays) || 30
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - days)

  const toIsoDate = date => date.toISOString().slice(0, 10)

  return {
    startDate: toIsoDate(start),
    endDate: toIsoDate(end)
  }
}

export function formatEntryDescription(guestCount) {
  const countLabel = formatCount(guestCount)
  return `${countLabel} ${OTA_BUSTER_ENTRY.description}`
}

export function daysToHours(days) {
  return Math.max(1, Number(days) || 1) * 24
}

export function hoursToDays(hours, fallbackDays) {
  if (hours == null || hours <= 0) {
    return fallbackDays
  }
  if (hours % 24 === 0) {
    return hours / 24
  }
  return Math.max(1, Math.round(hours / 24))
}

export function interpolateCampaignPreview(template, { firstName, hotelName } = {}) {
  return (template || '')
    .replace(/\{first\}/g, firstName || 'Joe')
    .replace(/\{hotel\}/g, hotelName || 'the hotel')
}

export function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US')
}

export function toPercent(rate) {
  return Math.round((Number(rate) || 0) * 100)
}

/**
 * Maps the analytics payload to per-step metrics keyed by step id.
 * The API serializes `category` as the numeric enum value, so the step slug is the
 * reliable join key; `category` is still honoured for payloads that send the enum name.
 * Steps without reporting data yet fall back to zeros instead of placeholder numbers.
 */
export function buildStepMetrics(analytics) {
  const byStep = {}
  const byCategory = {}
  ;(analytics?.steps || []).forEach(item => {
    if (item.step) {
      byStep[item.step] = item
    }
    byCategory[item.category] = item
  })

  return OTA_BUSTER_CAMPAIGN_STEPS.reduce((acc, step) => {
    const item = byStep[step.id] || byCategory[step.contentCategory]

    acc[step.id] = item
      ? {
          guests: item.guests || 0,
          emailsSent: item.emailsSent || 0,
          clickThroughRate: toPercent(item.clickThroughRate),
          identityCaptures: item.identityCaptures || 0,
          engagementRate: toPercent(item.oneTapAttachRate)
        }
      : { ...EMPTY_STEP_METRICS }

    return acc
  }, {})
}
